import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { User, Shield, Mail, Lock, Loader2, Phone } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('admin');
  
  // Admin login state
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState('');
  
  // Therapist login state
  // Modes: 'login' | 'verify-identity' | 'otp' | 'set-password'
  const [therapistMode, setTherapistMode] = useState('login');
  const [therapistName, setTherapistName] = useState('');
  const [therapistPassword, setTherapistPassword] = useState('');
  const [therapistPhone, setTherapistPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [therapistLoading, setTherapistLoading] = useState(false);
  const [therapistError, setTherapistError] = useState('');
  const [therapistToken, setTherapistToken] = useState('');

  // Admin login
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (adminLoading) return;
    setAdminLoading(true);
    setAdminError('');
    try {
      let useExternal = false;
      try {
        const configRes = await axios.get(`${API_URL}/api/external/config`);
        useExternal = configRes.data.use_external_api;
      } catch {
        useExternal = false;
      }
      if (useExternal) {
        try {
          const extRes = await axios.post(`${API_URL}/api/external/auth/login`, null, {
            params: { email: adminEmail.trim(), password: adminPassword }
          });
          if (extRes.data.token) localStorage.setItem('external_token', extRes.data.token);
        } catch {}
      }
      const response = await axios.post(`${API_URL}/api/auth/admin/login`, {
        email: adminEmail.trim(), password: adminPassword
      });
      if (response.data.token) {
        login(response.data.token, response.data.admin, 'admin');
        navigate('/admin', { replace: true });
      } else {
        setAdminError('Login failed. Please try again.');
      }
    } catch (error) {
      setAdminError(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setAdminLoading(false);
    }
  };

  // Therapist password login
  const handleTherapistLogin = async (e) => {
    e.preventDefault();
    setTherapistLoading(true);
    setTherapistError('');
    try {
      const response = await axios.post(`${API_URL}/api/auth/therapist/login`, {
        name: therapistName.trim(), password: therapistPassword
      });
      login(response.data.token, response.data.therapist, 'therapist');
      navigate('/therapist');
    } catch (error) {
      setTherapistError(error.response?.data?.detail || 'Login failed.');
    } finally {
      setTherapistLoading(false);
    }
  };

  // Step 1 of reset: Verify name + phone
  const handleVerifyIdentity = async (e) => {
    e.preventDefault();
    setTherapistLoading(true);
    setTherapistError('');
    try {
      await axios.post(`${API_URL}/api/auth/therapist/verify-identity`, {
        name: therapistName.trim(), phone_number: therapistPhone.trim()
      });
      setTherapistMode('otp');
    } catch (error) {
      setTherapistError(error.response?.data?.detail || 'Verification failed.');
    } finally {
      setTherapistLoading(false);
    }
  };

  // Step 2 of reset: Verify OTP
  const handleVerifyOTP = async () => {
    if (otpCode.length !== 6) return;
    setTherapistLoading(true);
    setTherapistError('');
    try {
      const response = await axios.post(`${API_URL}/api/auth/therapist/verify-otp`, {
        phone_number: therapistPhone.trim(), otp_code: otpCode
      });
      setTherapistToken(response.data.token);
      setTherapistMode('set-password');
    } catch (error) {
      setTherapistError(error.response?.data?.detail || 'Invalid OTP.');
      setOtpCode('');
    } finally {
      setTherapistLoading(false);
    }
  };

  // Step 3 of reset: Set password
  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setTherapistError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setTherapistError('Passwords do not match');
      return;
    }
    setTherapistLoading(true);
    setTherapistError('');
    try {
      await axios.post(`${API_URL}/api/auth/therapist/set-password`, {
        phone_number: therapistPhone.trim(), password: newPassword, token: therapistToken
      });
      const loginRes = await axios.post(`${API_URL}/api/auth/therapist/login`, {
        name: therapistName.trim(), password: newPassword
      });
      login(loginRes.data.token, loginRes.data.therapist, 'therapist');
      navigate('/therapist');
    } catch (error) {
      setTherapistError(error.response?.data?.detail || 'Failed to set password.');
    } finally {
      setTherapistLoading(false);
    }
  };

  const resetTherapistFlow = () => {
    setTherapistMode('login');
    setTherapistName('');
    setTherapistPassword('');
    setTherapistPhone('');
    setOtpCode('');
    setNewPassword('');
    setConfirmPassword('');
    setTherapistError('');
    setTherapistToken('');
  };

  const modeDescriptions = {
    'login': t('login.enterNamePassword') || 'Enter your name and password to login',
    'verify-identity': t('login.verifyIdentity') || 'Verify your identity with your name and phone number',
    'otp': t('login.enterAdminOtp') || 'Enter the OTP code provided by your admin',
    'set-password': t('login.createPassword') || 'Create a password for future logins',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher variant="outline" />
        </div>
        
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_spa-bookings-5/artifacts/syk2guux_Angel-Massage-logo-png.png" 
            alt="Angel Massage Logo"
            className="w-48 h-auto mx-auto mb-4"
          />
          <p className="text-gray-600">{t('login.staffPortal')}</p>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="therapist" className="flex items-center gap-2" data-testid="therapist-tab">
              <User className="w-4 h-4" />
              {t('login.therapist')}
            </TabsTrigger>
            <TabsTrigger value="admin" className="flex items-center gap-2" data-testid="admin-tab">
              <Shield className="w-4 h-4" />
              {t('login.admin')}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="therapist">
            <Card>
              <CardHeader>
                <CardTitle>{t('login.therapistLogin')}</CardTitle>
                <CardDescription>{modeDescriptions[therapistMode]}</CardDescription>
              </CardHeader>
              <CardContent>

                {/* Mode: Normal Login (Name + Password) */}
                {therapistMode === 'login' && (
                  <form onSubmit={handleTherapistLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="therapist-name">{t('login.yourName') || 'Your Name'}</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="therapist-name"
                          type="text"
                          placeholder={t('login.namePlaceholder') || 'e.g. Miw'}
                          value={therapistName}
                          onChange={(e) => setTherapistName(e.target.value)}
                          className="pl-10"
                          required
                          data-testid="therapist-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="therapist-password">{t('login.password')}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          id="therapist-password"
                          type="password"
                          placeholder="••••••••"
                          value={therapistPassword}
                          onChange={(e) => setTherapistPassword(e.target.value)}
                          className="pl-10"
                          required
                          data-testid="therapist-password-input"
                        />
                      </div>
                    </div>
                    
                    {therapistError && (
                      <p className="text-sm text-red-500" data-testid="therapist-error">{therapistError}</p>
                    )}
                    
                    <Button type="submit" className="w-full" disabled={therapistLoading} data-testid="therapist-login-btn">
                      {therapistLoading ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('login.loggingIn')}</>
                      ) : (
                        t('login.loginBtn')
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="link"
                      className="w-full text-sm"
                      onClick={() => {
                        setTherapistError('');
                        setTherapistMode('verify-identity');
                      }}
                      data-testid="forgot-password-btn"
                    >
                      {t('login.forgotPassword') || 'Forgot password? / First time login'}
                    </Button>
                  </form>
                )}

                {/* Mode: Verify Identity (Name + Phone) */}
                {therapistMode === 'verify-identity' && (
                  <form onSubmit={handleVerifyIdentity} className="space-y-4">
                    <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                      {t('login.identityInfo') || 'Enter your name and phone number registered with your admin to verify your identity.'}
                    </div>

                    <div className="space-y-2">
                      <Label>{t('login.yourName') || 'Your Name'}</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          type="text"
                          placeholder={t('login.namePlaceholder') || 'e.g. Miw'}
                          value={therapistName}
                          onChange={(e) => setTherapistName(e.target.value)}
                          className="pl-10"
                          required
                          data-testid="therapist-name-verify-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('login.phoneNumber') || 'Phone Number'}</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          type="tel"
                          placeholder={t('login.phonePlaceholder') || 'e.g. 0812345678'}
                          value={therapistPhone}
                          onChange={(e) => setTherapistPhone(e.target.value)}
                          className="pl-10"
                          required
                          data-testid="therapist-phone-input"
                        />
                      </div>
                    </div>
                    
                    {therapistError && (
                      <p className="text-sm text-red-500" data-testid="verify-identity-error">{therapistError}</p>
                    )}
                    
                    <Button type="submit" className="w-full" disabled={therapistLoading} data-testid="verify-identity-btn">
                      {therapistLoading ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('login.verifying') || 'Verifying...'}</>
                      ) : (
                        t('login.verifyAndContinue') || 'Verify & Continue'
                      )}
                    </Button>
                    
                    <Button type="button" variant="ghost" className="w-full" onClick={resetTherapistFlow} data-testid="back-to-login-btn">
                      {t('login.backToLogin') || 'Back to Login'}
                    </Button>
                  </form>
                )}

                {/* Mode: OTP Entry (after identity verified) */}
                {therapistMode === 'otp' && (
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                      {t('login.askAdminOtp') || 'Ask your admin to generate an OTP for you from the Admin Portal (Therapists tab).'}
                    </div>

                    <div className="space-y-2">
                      <Label>{t('login.enterOtpCode')}</Label>
                      <div className="flex justify-center">
                        <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode} data-testid="otp-input">
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                    </div>
                    
                    {therapistError && (
                      <p className="text-sm text-red-500 text-center" data-testid="otp-error">{therapistError}</p>
                    )}
                    
                    <Button
                      onClick={handleVerifyOTP}
                      className="w-full"
                      disabled={therapistLoading || otpCode.length !== 6}
                      data-testid="verify-otp-btn"
                    >
                      {therapistLoading ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('login.verifying') || 'Verifying...'}</>
                      ) : (
                        t('login.verifyLogin') || 'Verify & Continue'
                      )}
                    </Button>
                    
                    <Button variant="ghost" className="w-full" onClick={resetTherapistFlow} data-testid="back-to-login-otp-btn">
                      {t('login.backToLogin') || 'Back to Login'}
                    </Button>
                  </div>
                )}

                {/* Mode: Set Password (after OTP verified) */}
                {therapistMode === 'set-password' && (
                  <form onSubmit={handleSetPassword} className="space-y-4">
                    <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700">
                      {t('login.otpVerified') || 'OTP verified! Create a password for future logins.'}
                    </div>

                    <div className="space-y-2">
                      <Label>{t('login.newPassword') || 'New Password'}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          type="password"
                          placeholder={t('login.minChars') || 'At least 6 characters'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="pl-10"
                          required
                          minLength={6}
                          data-testid="new-password-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('login.confirmPassword') || 'Confirm Password'}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <Input
                          type="password"
                          placeholder={t('login.confirmPlaceholder') || 'Re-enter password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="pl-10"
                          required
                          minLength={6}
                          data-testid="confirm-password-input"
                        />
                      </div>
                    </div>
                    
                    {therapistError && (
                      <p className="text-sm text-red-500" data-testid="password-error">{therapistError}</p>
                    )}
                    
                    <Button type="submit" className="w-full" disabled={therapistLoading} data-testid="set-password-btn">
                      {therapistLoading ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('login.saving') || 'Saving...'}</>
                      ) : (
                        t('login.setPasswordLogin') || 'Set Password & Login'
                      )}
                    </Button>
                  </form>
                )}

              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="admin">
            <Card>
              <CardHeader>
                <CardTitle>{t('login.adminLogin')}</CardTitle>
                <CardDescription>{t('login.adminCredentials')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAdminLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('login.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input id="email" type="email" placeholder={t('login.emailPlaceholder')}
                        value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)}
                        className="pl-10" required data-testid="admin-email-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">{t('login.password')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input id="password" type="password" placeholder="••••••••"
                        value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)}
                        className="pl-10" required data-testid="admin-password-input" />
                    </div>
                  </div>
                  {adminError && (
                    <p className="text-sm text-red-500" data-testid="admin-error">{adminError}</p>
                  )}
                  <Button type="submit" className="w-full" disabled={adminLoading} data-testid="admin-login-btn">
                    {adminLoading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('login.loggingIn')}</>
                    ) : (
                      t('login.loginBtn')
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        <div className="mt-6 text-center">
          <Button variant="link" onClick={() => navigate('/')} data-testid="back-to-booking-btn">
            {t('login.backToBooking')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
